import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook to manage file drag and drop functionality.
 * It tracks when a file is being dragged over the component and handles the drop event.
 *
 * @param {object} options - The options for the hook.
 * @param {string|null} options.currentRoomId - The ID of the current room. Drag-drop is disabled if this is null.
 * @param {boolean} options.isCreatingNew - Whether a new room is being created. Drag-drop is disabled if true.
 * @param {boolean} [options.isQueueBusy=false] - Whether the message retry/send queue is busy. Drag-drop is disabled if true.
 * @param {function} options.onFilesDropped - Callback function to execute when files are dropped.
 * @returns {{isDraggingOver: boolean, dragHandlers: object}} - An object containing the dragging state and event handlers.
 */
export const useFileDragDrop = ({ currentRoomId, isCreatingNew, isQueueBusy = false, onFilesDropped }) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);

  // Safety reset: if room changes, reset drag state.
  useEffect(() => {
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
  }, [currentRoomId, isCreatingNew, isQueueBusy]);

  // Safety reset: if window loses focus, reset drag state.
  useEffect(() => {
    const handleBlur = () => {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, []);

  const onDragEnter = useCallback((e) => {
    // Don't do anything if no room is selected or in creation mode
    if (!currentRoomId || isCreatingNew || isQueueBusy) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Increment counter for each dragenter event
    dragCounterRef.current += 1;
    
    if (dragCounterRef.current > 0) {
      setIsDraggingOver(true);
    }
  }, [currentRoomId, isCreatingNew, isQueueBusy]);

  const onDragOver = useCallback((e) => {
    if (!currentRoomId || isCreatingNew || isQueueBusy) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  }, [currentRoomId, isCreatingNew, isQueueBusy]);

  const onDragLeave = useCallback((e) => {
    if (!currentRoomId || isCreatingNew || isQueueBusy) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Decrement counter for each dragleave event
    dragCounterRef.current -= 1;
    
    // Only hide overlay when counter reaches 0 (truly left the component)
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  }, [currentRoomId, isCreatingNew, isQueueBusy]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Reset counter and hide overlay
    dragCounterRef.current = 0;
    setIsDraggingOver(false);

    if (!currentRoomId || isCreatingNew || isQueueBusy) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFilesDropped(files);
    }
  }, [currentRoomId, isCreatingNew, isQueueBusy, onFilesDropped]);

  const dragHandlers = { onDragEnter, onDragLeave, onDragOver, onDrop };

  return { isDraggingOver, dragHandlers };
};
